import React from "react";
import Image from "next/image";
import Link from "next/link";
import { preload } from "react-dom";
import { Section } from "@/components/ui/Section";
import { Button, Typography, CalendlyWidget } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { env } from "@/lib/environment";
import { getCdnImageProps } from "@/lib/utils/cdn-image";
import { TrackingId } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { HeroButtons } from "@/components/ui/HeroButtons.client";

export interface ButtonConfig {
  text: string;
  href?: string; // If not provided, uses Calendly
  variant?: "primary" | "secondary" | "outlinePrimary" | "outlineSecondary" | "ghost";
  target?: string;
  arrow?: "none" | "hover" | "grow";
  disclaimer?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export interface ImageConfig {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  quality?: number; // Image quality (1-100, default: 60)
  unoptimized?: boolean; // Skip Next.js image optimization for CDN images
}

export interface VideoConfig {
  src: string;
  label: string;
  width?: number;
  height?: number;
  className?: string;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsinline?: boolean;
  poster?: string;
  /** Thick black frame around the video. Defaults to true for screen-bezel styling. */
  framed?: boolean;
}

export interface ClientLogo {
  logo: string;
  alt: string;
  width: number;
  height: number;
}

interface HeroSectionProps {
  id?: string;
  eyebrow?: React.ReactNode;
  // Allow rich formatting (e.g. <br />) in the title
  title: React.ReactNode;
  subtitle?: string;
  description: React.ReactNode; // Allow HTML elements in description
  image?: ImageConfig;
  video?: VideoConfig;
  customContent?: React.ReactNode; // For custom components like TVScreen
  buttons?: ButtonConfig[];
  buttonTrackingId?: {
    primaryId?: TrackingId;
    secondaryId?: TrackingId;
  };
  reviewSection?: boolean;
  showBackgroundGradient?: boolean;
  outerVideoUrl?: string;
  // Client logos section
  showClientLogos?: boolean;
  clientLogos?: ClientLogo[];
  clientLogosTitle?: string;
  clientLogosStats?: string;
  /** When true, show clientLogosTitle on desktop instead of clientLogosStats */
  clientLogosTitleOnDesktop?: boolean;
  // Optional: more compact, centered logo row for specific pages
  clientLogosCompact?: boolean;
  showCertifications?: boolean;
  certifications?: string[]; // Optional custom certifications array
  // Pass-through to Section for background styling and extra classes
  background?: "white" | "gray" | "red" | "gradient-red" | "gradient-light" | "transparent";
  sectionClassName?: string;
  // Optional alignment for text content (default left on desktop)
  align?: "left" | "center";
  // Optional extra React node rendered alongside primary buttons
  extraButtons?: React.ReactNode;
}

export function HeroSection({
  id,
  eyebrow,
  title,
  subtitle,
  description,
  image,
  video,
  customContent,
  buttons = [
    {
      text: "Start Free Trial",
      href: "https://console.pickcel.com/auth/register",
      variant: "primary",
    },
    { text: "Schedule My Demo", variant: "outlinePrimary" },
  ],
  buttonTrackingId,
  reviewSection,
  showBackgroundGradient = true,
  outerVideoUrl,
  showClientLogos = false,
  clientLogos = [],
  clientLogosTitle = "Trusted by businesses worldwide",
  clientLogosStats = "Trusted by 9,000+ Businesses • 150,000+ Screens • 70+ Countries",
  clientLogosTitleOnDesktop = false,
  showCertifications = false,
  certifications = ["SOC 2 Type II", "ISO Certified"], // Default certifications
  background = "transparent",
  sectionClassName,
  align = "left",
  clientLogosCompact = false,
  extraButtons,
}: HeroSectionProps) {
  const mediaAvailable = image || video || customContent;
  const isCentered = align === "center";

  if (image?.src) {
    preload(image.src, { as: "image", fetchPriority: "high" });
  }

  const heroImageProps = image
    ? getCdnImageProps(image.src, {
        priority: true,
        unoptimized: image.unoptimized,
        quality: image.quality,
      })
    : null;

  return (
    <>
      {showBackgroundGradient && (
        <div className="hidden lg:flex w-full pointer-events-none select-none absolute top-0 right-0 z-[-1] items-end justify-end">
          <Image
            src={`${env.NEXT_PUBLIC_CDN_URL}/images/digital-signage-player/right-gradient.hHJ3bWlVrj1o.webp`}
            alt="gradient background"
            title="gradient background"
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="max-w-full"
            width={960}
            height={987}
          />
        </div>
      )}
      <Section id={id} variant="default" background={background} className={`pt-24 lg:pt-[170px] pb-0 md:pb-8 relative scroll-mt-[140px] ${sectionClassName || ""}`}>
        <div
          className={`w-full grid grid-cols-1 ${
            mediaAvailable && !isCentered ? "lg:grid-cols-2" : ""
          } gap-8 items-center`}
        >
          <div
            className={`space-y-8 ${
              mediaAvailable
                ? isCentered
                  ? "text-center"
                  : "text-center lg:text-left"
                : "text-center w-full md:w-7/9 mx-auto"
            }`}
          >
            {eyebrow && eyebrow}
            {title && (
              <Typography variant="h1" as="h1">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="h2" as="h2">
                {subtitle}
              </Typography>
            )}
            {description && (
              <Typography variant="body" as="p">
                {description}
              </Typography>
            )}
            <HeroButtons
              buttons={buttons}
              buttonTrackingId={buttonTrackingId}
              extraButtons={extraButtons}
              className={
                align === "center"
                  ? "flex flex-col md:flex-row items-center md:items-start justify-center gap-6"
                  : `flex flex-col items-center ${
                      mediaAvailable
                        ? "lg:justify-start md:flex-row md:justify-center md:items-start "
                        : "md:flex-row md:justify-center md:items-start"
                    } gap-6`
              }
            />
            {reviewSection && (
              <div
                className={`flex flex-col lg:flex-row gap-6 md:gap-8 my-4 items-center ${
                  align === "center" ? "justify-center" : ""
                }`}
              >
                <Image
                src={env.NEXT_PUBLIC_CDN_URL + "/images/home/captera-g2.0K7YO82_kXp9.svg"}
                alt="Pickcel reviews on Capterra and G2"
                title="Pickcel reviews on Capterra and G2"
                width={320}
                height={38}
                priority
                fetchPriority="high"
                sizes="320px"
              />
              </div>
            )}
          </div>
          <div className="flex justify-center w-full relative">
            {customContent ? (
              customContent
            ) : image ? (
              <Image
                src={image.src}
                alt={image.alt}
                title={image.alt}
                width={image.width || 608}
                height={image.height || 344}
                className={`w-full h-auto ${image.className || ""}`}
                sizes="(max-width: 1024px) 100vw, 608px"
                {...heroImageProps!}
              />
            ) : (
              video && (
                <video
                  src={video.src}
                  width={video.width || 608}
                  height={video.height || 344}
                  aria-label={video.label}
                  poster={video.poster}
                  controls={video.controls ?? true}
                  autoPlay={video.autoplay ?? false}
                  loop={video.loop ?? true}
                  muted={video.muted ?? true}
                  playsInline={video.playsinline ?? true}
                  preload="metadata"
                  className={cn(
                    "w-full h-auto max-w-full",
                    video.framed === false
                      ? "rounded-xl shadow-lg"
                      : "rounded-sm shadow-lg border-3 border-black",
                    video.className,
                  )}
                  style={{
                    width: "100%",
                    maxWidth: `${video.width || 608}px`,
                    height: "auto",
                  }}
                />
              )
            )}

            {outerVideoUrl && (
              <div className="hidden md:block md:absolute w-full md:w-[70%] h-auto md:top-[55%] md:right-[-10px] border-4 border-black bg-black">
                <video
                  className="block w-full h-auto"
                  src={outerVideoUrl}
                  muted
                  preload="metadata"
                  autoPlay
                  loop
                  playsInline
                  aria-label="Digital Signage Player"
                ></video>
              </div>
            )}
          </div>
        </div>

        {/* Client Logos Section */}
        {showClientLogos && clientLogos.length > 0 && (
          <div className="flex flex-col w-full gap-[22px] mt-[44px] pb-[40px] overflow-hidden">
            <div className="flex w-full relative px-0 md:px-8 py-5 gap-2 md:gap-4 items-center flex-row justify-between max-w-full">
              <div className="flex-1 min-w-0 h-px bg-[#CCCCCC]"></div>
              <Typography variant="body" as="p" className={cn("shrink-0 text-text-primary font-normal text-center text-sm lg:text-base px-2", clientLogosTitleOnDesktop ? "flex" : "flex md:hidden")}>
                {clientLogosTitle}
              </Typography>
              {!clientLogosTitleOnDesktop && (
                <Typography
                  variant="body"
                  as="p"
                  className="shrink-0 text-text-primary font-normal hidden md:flex text-sm lg:text-base whitespace-nowrap px-2"
                >
                  {clientLogosStats}
                </Typography>
              )}
              <div className="flex-1 min-w-0 h-px bg-[#CCCCCC]"></div>
            </div>
            <div
              className={cn(
                "flex flex-wrap gap-4 justify-center items-center",
                clientLogos.length <= 3
                  ? "md:w-full md:max-w-[560px] md:mx-auto md:justify-between md:gap-0"
                  : "md:gap-[52px]",
                clientLogosCompact && clientLogos.length <= 5 && "max-w-[780px] mx-auto md:gap-10"
              )}
            >
              {clientLogos.map((client, index) => (
                <div className="mx-auto" key={index}>
                  <Image
                    src={client.logo}
                    alt={client.alt}
                    title={client.alt}
                    width={client.width}
                    height={client.height}
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 1024px) 140px, (min-width: 768px) 18vw, 30vw"
                    className={cn(
                      "md:aspect-auto h-10 md:h-auto grayscale",
                      index === 2 && "relative top-[4px]"
                    )}
                    // style={{ height: clientLogoImageClassName || "auto" }}
                  />
                </div>
              ))}
            </div>
            {showCertifications && certifications && certifications.length > 0 && (
              <div className="flex flex-wrap mt-[14px] gap-7 mb-[24px] justify-center">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex gap-2 items-center">
                  <Image
                    src={`${env.NEXT_PUBLIC_CDN_URL}/images/home/circle-check.tVJg_I2PKsbv.svg`}
                    alt="circle check"
                    aria-hidden="true"
                    width={16}
                    height={16}
                  />
                  <Typography
                    variant="small"
                    as="p"
                    className="font-normal text-xs text-text-secondary"
                  >
                      {cert}
                  </Typography>
                </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
    </>
  );
}
